"use client";

import axios from "axios";
import { useEffect, useState, useRef } from "react";
import { createWorker } from "tesseract.js";
import {
  CheckCircleIcon,
  DocumentIcon,
  SparklesIcon,
  XCircleIcon,
  PlayCircleIcon,
} from "@heroicons/react/24/outline";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button, type ButtonProps } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { YouTubeEmbed } from "@/app/_components/youtube-embed";
import { RequiredBuyPremiumButton } from "@/app/_components/required/buy-premium-button";

import { usePostAnalyze } from "@/queries/mutations/use-post-analyze";
import { usePostRegistryRead } from "@/queries/mutations/use-post-registry-read";
import { usePostValidateRegistry } from "@/queries/mutations/use-post-validate-registry";

import { BusinessError } from "@/lib/errors";

import { convertToImage, loadFile } from "@/helper/pdf";

import type { House } from "@/types";

const convertToText = async (
  images: string[],
  onProgress: (progress: number) => void
): Promise<string> => {
  try {
    let completedTasks = 0;
    const totalTasks = images.length;

    const createWorkerForImage = async (image: string): Promise<string> => {
      const worker = await createWorker("por", 1);

      const {
        data: { text },
      } = await worker.recognize(image);

      await worker.terminate();

      completedTasks += 1;
      const progress = Math.round((completedTasks / totalTasks) * 100);
      onProgress(progress);

      return text;
    };

    onProgress(0);

    const results = await Promise.all(
      images.map((image) => createWorkerForImage(image))
    );

    onProgress(100);

    return results.join(" ").replace(/\n/g, "").replace(/\s+/g, " ").trim();
  } catch (error) {
    console.error("Error ao analisar texto: ", error);
    throw new Error("Error ao analisar texto");
  }
};

const STEP_RANGES = {
  load: { start: 0, end: 20 },
  convert: { start: 20, end: 60 },
  save: { start: 60, end: 100 },
};

const steps = [
  { id: "load", title: "Carregando arquivo", status: "pending" },
  {
    id: "convert",
    title: "Convertando arquivo para texto",
    status: "pending",
  },
  { id: "analyze", title: "Analisando arquivo", status: "pending" },
];

const scaleProgress = (progress: number, start: number, end: number) => {
  return start + (progress * (end - start)) / 100;
};

type Action = "registry" | "diligence";

interface CreateRegistryProps
  extends Pick<ButtonProps, "size" | "className" | "variant"> {
  data: House;
  url?: string;
  action?: Action;
}

const actions = {
  registry: {
    label: "Analisar matrícula com IA",
    Icon: SparklesIcon,
  },
  diligence: {
    label: "Diligência prévia do imóvel",
    Icon: DocumentIcon,
  },
};

interface RegistryUrlInputProps {
  bankName?: string;
  onFileUpload: (file: ArrayBuffer) => void;
}

function RegistryUrlInput({ bankName, onFileUpload }: RegistryUrlInputProps) {
  const [showVideo, setShowVideo] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    if (!file) {
      return;
    }

    // Check if file is a PDF
    if (file.type !== "application/pdf") {
      toast({
        variant: "destructive",
        title: "Formato inválido",
        description: "Apenas arquivos PDF são aceitos.",
      });
      return;
    }

    setSelectedFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast({
        variant: "destructive",
        title: "Formato inválido",
        description: "Apenas arquivos PDF são aceitos.",
      });
      return;
    }

    setSelectedFile(file);
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const fileUpload = async () => {
    if (!selectedFile) return;

    try {
      setIsUploading(true);

      const fileBuffer = await selectedFile.arrayBuffer();
      onFileUpload(fileBuffer);
    } catch (error) {
      console.error("Erro ao processar arquivo:", error);
      toast({
        variant: "destructive",
        title: "Erro ao processar arquivo",
        description: "Não foi possível processar o arquivo selecionado.",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const toggleVideo = () => {
    setShowVideo(!showVideo);
  };

  return (
    <div className="flex flex-col gap-8">
      {bankName?.toLowerCase() !== "caixa" && (
        <div className="rounded-md bg-yellow-300/20 p-4 flex flex-col gap-2">
          <p className="text-sm">
            <span className="font-medium">Importante:</span> Para imóveis do
            banco <span className="capitalize">{bankName}</span>, você precisa
            obter a matrícula manualmente no site do leiloeiro.
          </p>
          <p className="text-sm">
            A CAIXA é o único banco que disponibiliza as matrículas diretamente
            e por esse motivo em imóveis da CAIXA não é necessário informar a
            matrícula.
          </p>
          <p className="text-sm">
            Caso não saiba como obter a matrícula, use o botão de ajuda para
            instruções.
          </p>
        </div>
      )}
      <div className="flex flex-col gap-6">
        {/* File Upload Drop Area */}
        <div
          className={`
            border-2 border-dashed rounded-lg p-6 cursor-pointer
            flex flex-col items-center justify-center text-center
            ${isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/20"}
            ${selectedFile ? "bg-primary/5" : ""}
            transition-colors duration-200
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleBrowseClick}
        >
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept=".pdf,application/pdf"
            onChange={handleFileChange}
          />

          <div className="mb-4 text-primary bg-primary/10 p-3 rounded-full">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-10 w-10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M14 3v4a1 1 0 0 0 1 1h4" />
              <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
              <circle cx="12" cy="13" r="3" />
              <line x1="12" y1="16" x2="12" y2="16" />
              <line x1="12" y1="10" x2="12" y2="10" />
              <line x1="12" y1="13" x2="12" y2="13" />
              <line x1="9" y1="13" x2="15" y2="13" />
            </svg>
          </div>
          {selectedFile ? (
            <div>
              <p className="text-lg font-medium">Arquivo selecionado</p>
              <p className="text-sm text-muted-foreground">
                {selectedFile.name}
              </p>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  fileUpload();
                }}
                disabled={isUploading}
                className="mt-4"
              >
                {isUploading ? "Processando..." : "Analisar PDF"}
              </Button>
            </div>
          ) : (
            <>
              <p className="text-lg font-medium">
                Busque o arquivo PDF da matrícula
              </p>
              <p className="text-sm text-muted-foreground">
                ou arraste e solte
              </p>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex justify-start">
          <Button
            variant="outline"
            onClick={toggleVideo}
            className="flex items-center gap-2"
          >
            <PlayCircleIcon className="w-4 h-4" />
            {showVideo ? "Ocultar Vídeo" : "Ajuda em Vídeo"}
          </Button>
        </div>

        {showVideo ? (
          <div className="border rounded-md overflow-hidden bg-muted/20">
            <div className="p-4 flex flex-col gap-1">
              <h3 className="text-lg font-medium">Como obter a matrícula</h3>
              <p className="text-sm text-muted-foreground">
                Assista ao vídeo abaixo para aprender como obter a matrícula no
                site do banco ou leiloeiro.
              </p>
            </div>
            <div className="w-full h-11/12 aspect-video bg-muted">
              <YouTubeEmbed
                videoid="wJyVcJpsRoY"
                playlabel="Como obter a matrícula"
                params="rel=0&autoplay=1"
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export const CreateRegistry = ({
  data,
  url,
  size,
  variant,
  action = "registry",
  className,
}: CreateRegistryProps) => {
  const { toast } = useToast();

  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [registryUrl, setRegistryUrl] = useState(url || "");
  const [error, setError] = useState<{ title: string; message: string } | null>(
    null
  );

  const { label, Icon } = actions[action];

  const mutationValidate = usePostValidateRegistry();
  const mutationRead = usePostRegistryRead();
  const mutationCreate = usePostAnalyze();

  const updateStepProgress = async (step: number, stepProgress: number) => {
    const range =
      step === 0
        ? STEP_RANGES.load
        : step === 1
          ? STEP_RANGES.convert
          : STEP_RANGES.save;

    const scaledProgress = scaleProgress(stepProgress, range.start, range.end);
    setProgress(scaledProgress);

    if (stepProgress === 100) {
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  };

  const loadRegistry = async (urlToUse = registryUrl) => {
    if (!isOpen || isLoading || !urlToUse.trim()) return;

    try {
      setIsLoading(true);
      setIsComplete(false);
      setError(null);
      setRegistryUrl(urlToUse);

      setCurrentStep(0);
      await updateStepProgress(0, 0);

      mutationValidate.mutate(
        {
          banco: data.banco,
          numero_do_leilao: data.numero_do_leilao,
          type: "matricula",
        },
        {
          onSuccess: async (validateResult) => {
            if (validateResult.valido && validateResult.texto) {
              setCurrentStep(1);

              await Promise.all([
                updateStepProgress(0, 100),
                updateStepProgress(1, 100),
              ]);

              setCurrentStep(2);

              mutationCreate.mutate(
                {
                  property: data,
                  text: validateResult.texto,
                  type: "matricula",
                },
                {
                  onSuccess: async (data) => {
                    const { registro } = data;

                    if (registro) {
                      await updateStepProgress(2, 100);
                      setIsComplete(true);
                      setIsLoading(false);

                      toast({
                        variant: "default",
                        title: "Análise de matrícula concluída.",
                      });

                      return;
                    }
                  },
                }
              );
            } else {
              mutationRead.mutate(
                { url: urlToUse, type: "matricula" },
                { onSuccess: processFile }
              );
            }
          },
        }
      );
    } catch (error) {
      console.error("Erro ao analisar matrícula: ", error);

      let errorMessage = "Erro desconhecido ao analisar matrícula.";

      if (axios.isAxiosError(error)) {
        // For arraybuffer responses, we need to convert the response to text to get the error message
        if (error.response?.data instanceof ArrayBuffer) {
          const decoder = new TextDecoder("utf-8");
          const text = decoder.decode(error.response.data);
          try {
            const errorData = JSON.parse(text);
            errorMessage = errorData.error || errorMessage;
          } catch (e) {
            errorMessage = "Erro ao processar resposta do servidor";
          }
        } else {
          errorMessage = error.response?.data?.error || error.message;
        }
      } else if (error instanceof BusinessError) {
        errorMessage = error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setIsLoading(false);

      setError({
        title: "Erro na análise",
        message: errorMessage,
      });
    }
  };

  const processFile = async (fileBuffer: ArrayBuffer) => {
    try {
      await updateStepProgress(0, 50);

      const pdf = await loadFile(fileBuffer);
      await updateStepProgress(0, 100);

      setCurrentStep(1);
      await updateStepProgress(1, 0);
      const images = await convertToImage(pdf);
      await updateStepProgress(1, 30);

      const text = await convertToText(images, async (imageProgress) => {
        const convertProgress = 30 + imageProgress * 0.7;
        await updateStepProgress(1, convertProgress);
      });

      if (!text?.trim()) {
        throw new Error("Não foi possível extrair texto do documento");
      }

      await updateStepProgress(1, 100);

      setCurrentStep(2);
      await updateStepProgress(2, 0);

      mutationCreate.mutate(
        {
          property: data,
          text,
          type: "matricula",
        },
        {
          onSuccess: async (result) => {
            const { registro } = result;

            if (registro) {
              await updateStepProgress(2, 100);
              setIsComplete(true);
              setIsLoading(false);

              toast({
                variant: "default",
                title: "Análise de matrícula concluída.",
              });
            }
          },
        }
      );
    } catch (pdfError) {
      setError({
        title: "Erro ao processar PDF",
        message:
          "O arquivo não pôde ser processado. Verifique se é um PDF válido e tente novamente.",
      });
    }
  };

  const onOpenChange = (open: boolean) => {
    setIsOpen(open);

    if (!open) {
      setCurrentStep(0);
      setProgress(0);
      setIsLoading(false);
      setIsComplete(false);
      setError(null);
    }
  };

  const fileUpload = (fileBuffer: ArrayBuffer) => {
    if (!isOpen || isLoading) return;

    try {
      setIsLoading(true);
      setIsComplete(false);
      setError(null);

      setCurrentStep(0);
      updateStepProgress(0, 0);

      processFile(fileBuffer);
    } catch (error) {
      console.error("Erro ao processar arquivo:", error);
      setIsLoading(false);
      setError({
        title: "Erro ao processar arquivo",
        message: "Não foi possível processar o arquivo selecionado.",
      });
    }
  };

  useEffect(() => {
    if (isOpen && url) {
      loadRegistry();
    }
  }, [isOpen, url]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <RequiredBuyPremiumButton>
          <Button variant={variant} size={size} className={className}>
            <Icon className="w-4 h-4" aria-hidden="true" />
            <span className="ml-2">{label}</span>
          </Button>
        </RequiredBuyPremiumButton>
      </DialogTrigger>
      <DialogContent className="overflow-auto w-full sm:max-w-2xl h-full sm:max-h-[95%] sm:min-h-[400px] sm:h-auto">
        <DialogHeader>
          <DialogTitle>
            {error
              ? "Erro na análise"
              : isComplete
                ? "Análise de matrícula concluída"
                : isLoading
                  ? "Análise de matrícula em andamento"
                  : "Análise de matrícula"}
          </DialogTitle>
          <DialogDescription>
            {error
              ? error.message
              : isComplete
                ? "A análise da matrícula foi concluída com sucesso. Você já pode fechar esta janela."
                : isLoading
                  ? "Estamos processando os dados da matrícula. Ao final, você terá acesso a todas as informações de forma clara, prática e organizada."
                  : "Insira a matrícula para análise ou utilize o vídeo de instruções para obter ajuda."}
          </DialogDescription>
          <div className="flex flex-col gap-12 pt-6">
            {error ? (
              <div className="flex flex-col items-center justify-center gap-4 py-8">
                <XCircleIcon className="w-16 h-16 text-destructive animate-in zoom-in" />
                <p className="text-lg font-medium text-destructive text-center">
                  {error.title}
                </p>
                <p className="text-muted-foreground text-center">
                  {error.message}
                </p>
                <p className="text-muted-foreground text-center">
                  Nosso time já foi notificado, iremos investigar o problema o
                  mais rápido possível.
                </p>
              </div>
            ) : isComplete ? (
              <div className="flex flex-col items-center gap-4 py-8">
                <CheckCircleIcon className="w-16 h-16 text-primary animate-in zoom-in" />
                <p className="text-lg font-medium text-primary">
                  Processamento concluído!
                </p>
                <p className="text-center text-muted-foreground">
                  Você será redirecionado para a matrícula em segundos. Caso
                  isso não ocorra, clique no botão abaixo.
                </p>
              </div>
            ) : isLoading ? (
              <div className="space-y-4">
                {steps.map((step, index) => {
                  const isActive = currentStep === index;
                  const isComplete = currentStep > index;

                  return (
                    <div
                      key={step.id}
                      className={`relative transition-all duration-500 ease-in-out ${
                        isActive
                          ? "opacity-100"
                          : isComplete
                            ? "opacity-50"
                            : "opacity-30"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-6 h-6 rounded-full flex items-center justify-center text-sm transition-colors duration-300 ${
                            isComplete
                              ? "bg-primary text-primary-foreground"
                              : isActive
                                ? "bg-primary/10 text-primary"
                                : "bg-muted text-muted-foreground"
                          } `}
                        >
                          {isComplete ? "✓" : index + 1}
                        </div>
                        <span
                          className={`font-medium ${
                            isActive ? "text-primary" : "text-foreground"
                          }`}
                        >
                          {step.title}
                        </span>
                      </div>

                      {isActive && (
                        <div className="mt-2 ml-9">
                          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary transition-all duration-300 ease-out"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <RegistryUrlInput
                bankName={data.banco}
                onFileUpload={fileUpload}
              />
            )}
            {isLoading && !isComplete && !error && (
              <>
                <p className="text-sm text-muted-foreground text-center">
                  Por favor, não feche a página enquanto estiver carregando
                </p>
                <div className="flex flex-col gap-2">
                  <p className="text-xs text-muted-foreground">
                    Navegadores modernos suportados: Chrome e Firefox.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Navegadores legados suportados: Opera, Edge, Chrome 110+,
                    Firefox ESR+ e Safari 16.4+.
                  </p>
                </div>
              </>
            )}
            <div id="container" />
          </div>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
